import { useCallback, useReducer, useRef } from 'react';

/**
 * Undo and redo for an editor's working copy (KPR-348): a stack of what the
 * value WAS before each change, one step per change a person made.
 *
 *   const history = useUndoHistory(emptyBody);
 *   history.present;                                   // the value on screen
 *   history.commit(current => next, { label: 'move' }); // one step
 *   history.undo();   // -> { label } of the step undone, or null
 *   history.reset(stored);                              // a load, not an edit
 *   history.rebase(step => next);                       // a change no undo may reach
 *
 * Why a ref and not `useState`: a commit reads the LATEST value synchronously,
 * so two edits in one tick (a drop that inserts and selects, a key repeat) both
 * see each other, and there is never a side effect inside a state updater
 * (which React may run twice).
 *
 * The rules, each of them a decision:
 * - **A change that changes nothing is no step.** A drag that lands where it
 *   began, a nudge against the page edge, a typed value equal to the stored
 *   one: nothing to undo, and the redo stack is kept.
 * - **A new edit clears redo**, as everywhere.
 * - **Consecutive commits with the same `key`, within `within` ms, are ONE
 *   step** (typing a number, holding an arrow key). A merge that arrives back
 *   at the step's starting value removes the step: net nothing happened.
 *   `within: Infinity` makes the key a gesture rather than a burst.
 * - **`reset` is not an edit.** A stored layout arriving, a tier being chosen:
 *   both stacks are emptied and the value is the new baseline. An editor
 *   closing needs nothing: the history is its component's own.
 * - **`rebase` is not an edit either, and keeps both stacks.** Something changed
 *   on the server that no step may bring back (a page layout's form and its
 *   paper, KPR-1860): `fn` rewrites the value and every value on the stacks, so
 *   undo and redo go on working around the change.
 * - **Capped at `limit` steps** (100); the oldest goes first.
 *
 * The one undo engine of the three layout editors (the form layout editor
 * moved onto it in KPR-1010; its older `utils/editHistory.js` is gone).
 *
 * Nothing here is stored or sent. The history lives as long as the component
 * that holds it, so closing an editor clears it.
 */

export const UNDO_LIMIT = 100;
export const MERGE_WINDOW_MS = 1000;

const defaultSame = (a, b) => a === b || JSON.stringify(a) === JSON.stringify(b);

export default function useUndoHistory(initial, { limit = UNDO_LIMIT, same = defaultSame } = {}) {
	const store = useRef(null);
	if (store.current === null) {
		store.current = {
			present: typeof initial === 'function' ? initial() : initial,
			past: [],     // [{ before, label }] oldest first: the value before the edit
			future: [],   // [{ after, label }] the next redo is the last
			last: null,   // { key, at } of the commit a merge may join
		};
	}
	const [, rerender] = useReducer(count => count + 1, 0);

	const commit = useCallback((change, { label = 'change', key = null, within = MERGE_WINDOW_MS } = {}) => {
		const state = store.current;
		const next = typeof change === 'function' ? change(state.present) : change;
		if (next === state.present || same(next, state.present)) return state.present;
		const now = Date.now();
		const joins = key !== null && state.last && state.last.key === key && now - state.last.at <= within && state.past.length > 0;
		if (joins) {
			state.present = next;
			state.last = { key, at: now };
			// Back where the step began: it never happened.
			if (same(next, state.past[state.past.length - 1].before)) {
				state.past.pop();
				state.last = null;
			}
		} else {
			state.past.push({ before: state.present, label });
			if (state.past.length > limit) state.past.shift();
			state.present = next;
			state.last = key === null ? null : { key, at: now };
		}
		state.future = [];
		rerender();
		return next;
	}, [same, limit]);

	const undo = useCallback(() => {
		const state = store.current;
		const step = state.past.pop();
		if (!step) return null;
		state.future.push({ after: state.present, label: step.label });
		state.present = step.before;
		state.last = null;
		rerender();
		return { label: step.label };
	}, []);

	const redo = useCallback(() => {
		const state = store.current;
		const step = state.future.pop();
		if (!step) return null;
		state.past.push({ before: state.present, label: step.label });
		state.present = step.after;
		state.last = null;
		rerender();
		return { label: step.label };
	}, []);

	const reset = useCallback((seed) => {
		const state = store.current;
		state.present = typeof seed === 'function' ? seed(state.present) : seed;
		state.past = [];
		state.future = [];
		state.last = null;
		rerender();
	}, []);

	const rebase = useCallback((fn) => {
		const state = store.current;
		state.present = fn(state.present);
		state.past = state.past.map(step => ({ ...step, before: fn(step.before) }));
		state.future = state.future.map(step => ({ ...step, after: fn(step.after) }));
		state.last = null;
		rerender();
		return state.present;
	}, []);

	/** The latest value, for a handler that must not read a stale render. */
	const current = useCallback(() => store.current.present, []);

	const state = store.current;
	return {
		present: state.present,
		canUndo: state.past.length > 0,
		canRedo: state.future.length > 0,
		steps: state.past.length,
		commit,
		undo,
		redo,
		reset,
		rebase,
		current,
	};
}
